/**
 * 내 단어장(총괄 DEV-I-013, 디자인 단어장 v0.1). DOM 없이 단위 테스트한다.
 *
 * - 서버(growing-server v0.10.1) GET /me/words · GET /me/words/:wordId 응답만 쓴다. 화면은 수집을 실행하지 않는다.
 * - 목록은 wordId, 출처는 sourceId로 구분한다. 표제어가 같아도 의미 항목(wordId)이 다르면 합치지 않는다.
 * - 순서는 API가 보낸 그대로(최근순·학습순 같은 뜻을 붙이지 않음).
 * - 출처 수는 오답 횟수·학습 횟수가 아니다(문구 '출처 n개'). 목록은 2개 이상일 때만, 상세는 1개도.
 * - 서버의 제공 기록은 읽음·암기 완료가 아니다(화면에 그런 표시를 하지 않음).
 */
import type { ApiResult } from './transport.js';
import type { BatchOutcome } from './result-batch.js';

export interface WordOrigin { readonly assignmentTitle: string; readonly releaseTitle: string; readonly displayNumber: string; readonly attemptId: string }
export interface WordSource { readonly sourceId: string; readonly form: string; readonly posInContext: string; readonly meaningInContext: string; readonly origin: WordOrigin }
export interface WordItem { readonly wordId: string; readonly headword: string; readonly meaning: string; readonly sources: readonly WordSource[] }

const str = (v: unknown): v is string => typeof v === 'string';

function parseSource(x: unknown): WordSource | null {
  if (x === null || typeof x !== 'object') return null;
  const s = x as Record<string, unknown>;
  const o = s['origin'] as Record<string, unknown> | null | undefined;
  if (!str(s['sourceId']) || !str(s['form']) || !str(s['posInContext']) || !str(s['meaningInContext']) || o === null || typeof o !== 'object') return null;
  if (!str(o['assignmentTitle']) || !str(o['releaseTitle']) || !str(o['displayNumber']) || !str(o['attemptId'])) return null;
  return { sourceId: s['sourceId'], form: s['form'], posInContext: s['posInContext'], meaningInContext: s['meaningInContext'],
    origin: { assignmentTitle: o['assignmentTitle'], releaseTitle: o['releaseTitle'], displayNumber: o['displayNumber'], attemptId: o['attemptId'] } };
}

/** 단어 항목 하나. 필드가 빠졌거나 출처가 없으면 null(화면에 반쯤 그리지 않음) */
export function parseWord(x: unknown): WordItem | null {
  if (x === null || typeof x !== 'object') return null;
  const w = x as Record<string, unknown>;
  if (!str(w['wordId']) || !str(w['headword']) || !str(w['meaning']) || !Array.isArray(w['sources']) || w['sources'].length === 0) return null;
  const sources: WordSource[] = [];
  const seen = new Set<string>();
  for (const s of w['sources']) {
    const p = parseSource(s);
    if (p === null || seen.has(p.sourceId)) return null;
    seen.add(p.sourceId); sources.push(p);
  }
  return { wordId: w['wordId'], headword: w['headword'], meaning: w['meaning'], sources };
}

/** 목록 응답. 순서는 그대로 */
export function parseWordList(body: Record<string, unknown>): WordItem[] | null {
  const ws = body['words'];
  if (!Array.isArray(ws)) return null;
  const out: WordItem[] = [];
  const seen = new Set<string>();
  for (const x of ws) {
    const w = parseWord(x);
    if (w === null || seen.has(w.wordId)) return null;
    seen.add(w.wordId); out.push(w);
  }
  return out;
}

export type WordsDecision<T> =
  | { readonly kind: 'ok'; readonly data: T }
  | { readonly kind: 'relogin' }
  /** 403: 원인(계정 종류 등)을 단정하지 않는다 */
  | { readonly kind: 'denied' }
  /** 상세 404: '이 단어를 지금은 볼 수 없어요.' */
  | { readonly kind: 'notfound' }
  | { readonly kind: 'error'; readonly cause: 'network' | 'server' | 'timeout' | 'malformed' | 'word_mismatch' };

function fromResult(r: ApiResult): { kind: 'http'; status: number; body: Record<string, any> } | { kind: 'network' } {
  return r.kind === 'http' ? r : { kind: 'network' };
}

/** 조회 하나의 결과(확인 제한 포함)를 판정한다. asked: 상세면 요청한 wordId, 목록이면 null */
export function decideWords(asked: string | null, outcome: BatchOutcome): WordsDecision<WordItem[] | WordItem> {
  if (outcome.kind === 'timeout') return { kind: 'error', cause: 'timeout' };
  const r = fromResult(outcome.kind === 'early' ? outcome.result : outcome.grading);
  if (r.kind === 'network') return { kind: 'error', cause: 'network' };
  if (r.status === 401) return { kind: 'relogin' };
  if (r.status === 403) return { kind: 'denied' };
  if (r.status === 404) return asked !== null ? { kind: 'notfound' } : { kind: 'error', cause: 'server' };
  if (r.status !== 200) return { kind: 'error', cause: 'server' };
  if (asked === null) {
    const list = parseWordList(r.body);
    return list === null ? { kind: 'error', cause: 'malformed' } : { kind: 'ok', data: list };
  }
  const w = parseWord(r.body);
  if (w === null) return { kind: 'error', cause: 'malformed' };
  // 상세 응답의 wordId도 요청한 것과 같아야 한다
  if (w.wordId !== asked) return { kind: 'error', cause: 'word_mismatch' };
  return { kind: 'ok', data: w };
}

/** 고른 형태 칩: 표제어와 다른 form만, 처음 나온 순서, 중복 없음 */
export function formChips(w: WordItem): string[] {
  const out: string[] = [];
  for (const s of w.sources) if (s.form !== w.headword && !out.includes(s.form)) out.push(s.form);
  return out;
}

/** '출처 n개': 목록은 2개 이상일 때만, 상세는 1개도. 오답·학습 횟수가 아니다 */
export function sourceCountLabel(n: number, where: 'list' | 'detail'): string | null {
  if (where === 'list' && n < 2) return null;
  return `출처 ${n}개`;
}

/** 출처 카드의 형태 줄: 고른 형태와 표제어가 같으면 표제어만 */
export function formLine(s: WordSource, headword: string): string {
  return s.form === headword ? `고른 보기 형태 ${s.form}` : `고른 보기 형태 ${s.form} → 표제어 ${headword}`;
}

/** 요청을 보낼 때의 문맥. 응답을 기다린 뒤 지금과 모두 같아야 반영한다(늦은 응답 차단, 취소 여부와 별개) */
export interface WordsReqCtx {
  readonly gen: number;
  readonly me: string | null;
  /** 화면 값 객체(목록·상세를 열 때마다 새로 만든다) */
  readonly screen: object;
  readonly seq: number;
  /** 상세면 요청한 wordId, 목록이면 null */
  readonly wordId: string | null;
}
export function wordsResponseUsable(sent: WordsReqCtx, now: WordsReqCtx | null): boolean {
  return now !== null && sent.gen === now.gen && sent.me === now.me && sent.screen === now.screen && sent.seq === now.seq && sent.wordId === now.wordId;
}

export const WORDS_CHECK_MS = 30_000;
